/**
 * Aggregate outcomes and failed-MRN detail for a durable ingestion parent.
 */

import type { JobStatusResponse } from "../../api/clinicalTrialsApiTypes";

/**
 * Render durable outcome counters and the failed-MRN audit list.
 *
 * @param props.job - Selected durable parent
 * @returns Outcome summary section
 */
export function JobOutcomeSummary({ job }: { job: JobStatusResponse }) {
  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <h3 className="mb-2 text-sm font-semibold">Patient outcomes</h3>
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <Outcome label="Success" value={job.success_count} />
        <Outcome label="No data" value={job.no_data_count} />
        <Outcome label="Retrying" value={job.retryable_failed_count} />
        <Outcome label="Permanent" value={job.permanent_failed_count} />
      </div>
      {!!job.failed_mrns.length && (
        <div className="mt-3 max-h-48 overflow-y-auto rounded-md border border-red-100">
          {job.failed_mrns.map((outcome) => (
            <div
              key={outcome.mrn}
              className="grid grid-cols-[1fr_auto] gap-2 border-b border-red-50 px-3 py-2 text-xs last:border-0"
            >
              <span>
                <span className="font-mono font-medium">{outcome.mrn}</span>
                {outcome.error_message && (
                  <span className="mt-0.5 block text-red-700">
                    {outcome.error_message}
                  </span>
                )}
              </span>
              <span className="text-right text-gray-500">
                {outcome.status.replace("_", " ")}
                <span className="block">attempt {outcome.attempt_count}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Render one compact outcome counter.
 *
 * @param props - Counter label and value
 * @returns Labeled counter card
 */
function Outcome({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-gray-50 p-2">
      <span className="block text-gray-500">{label}</span>
      <span className="text-base font-semibold">{value}</span>
    </div>
  );
}
